import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  CancelSubscriptionPayload,
  Subscription,
  SubscriptionPlan,
} from "../../interfaces/SubscriptionObject";
import { getRequest, postRequest } from "../http/httpRequest";
import { unwrapEnvelope } from "../helpers/ApiEnvelopeHelper";
import { buildCancelPayload, buildSubscribePayload } from "../converters/uiToBackend/SubscriptionConvertor";
import {
  convertSubscriptionEnvelope,
  convertSubscriptionPlansList,
  convertUserSubscriptionsList,
} from "../converters/backendToUi/subscriptionConvertor";

// All subscription endpoints are bearer token + the new-style envelope;
// subscribe/cancel are POST, subscriptions/user is a GET.
const BEARER = true;

/** Starts (or re-triggers) auto-pay setup for a business's subscription. */
async function subscribeBusiness(
  businessId: string,
  planCode: string
): Promise<Subscription> {
  const response = await postRequest(
    `${ServiceEndpoints.SUBSCRIPTION_BUSINESS_BASE}/${businessId}/subscribe`,
    buildSubscribePayload(planCode),
    BEARER
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return convertSubscriptionEnvelope(contents);
}

/** All subscriptions across every business the current user owns. */
async function getUserSubscriptions(): Promise<Subscription[]> {
  const response = await getRequest(ServiceEndpoints.SUBSCRIPTION_USER, BEARER);
  const contents = await unwrapEnvelope<unknown>(response);
  return convertUserSubscriptionsList(contents);
}

/** Cancels a subscription (default: at period end — see CancelSubscriptionPayload). */
async function cancelSubscription(payload: CancelSubscriptionPayload): Promise<Subscription> {
  const response = await postRequest(
    ServiceEndpoints.SUBSCRIPTION_CANCEL,
    buildCancelPayload(payload),
    BEARER
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return convertSubscriptionEnvelope(contents);
}

/** The catalog of plans a business can subscribe to (small/medium/large/etc.)
 *  — distinct from getUserSubscriptions, which returns a business's own
 *  active/past subscription record, not the available choices. */
async function getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  const response = await getRequest(ServiceEndpoints.SUBSCRIPTION_PLANS, BEARER);
  const contents = await unwrapEnvelope<unknown>(response);
  return convertSubscriptionPlansList(contents);
}

const SubscriptionApiService = {
  subscribeBusiness,
  getUserSubscriptions,
  cancelSubscription,
  getSubscriptionPlans,
};

export default SubscriptionApiService;
